import { OptimizelyDecision } from '@optimizely/react-sdk';

import Rollbar from 'src/analytics/rollbar';
import UserModel from 'src/models/UserModel';
import { flagsToTrack } from 'src/services/flags/constants/optimizely';
import { OptimizelyEventName, OptimizelyKey, OptimizelyWebEvent } from './types';

/**
 * Tracks an event in Optimizely Web Experimentation
 * @param eventName The name of the event to track
 * @param eventTags Optional tags for the event (including revenue/value)
 * @param properties Optional additional properties for the event
 *
 */
export const trackOptimizelyWebExp = (
  eventName: OptimizelyEventName,
  properties?: {
    amount?: number | string;
    currency?: string;
    [key: string]: any;
  },
  eventTags?: {
    revenue?: number;
    value?: number;
    [key: string]: any;
  }
): void => {
  try {
    if (!window.optimizely) {
      return;
    }

    const event: OptimizelyWebEvent = {
      type: 'event',
      eventName,
      tags: eventTags || {},
      properties: properties || {}
    };

    window.optimizely.push(event);
  } catch (error) {
    Rollbar.trackError('Failed to track Web Optimizely event:', error, {
      eventName,
      eventTags,
      properties
    });
  }
};

export const handleExperimentTracking = (flagName: OptimizelyKey, decision: OptimizelyDecision) => {
  if (!flagsToTrack.includes(flagName) || !decision.enabled) {
    return;
  }

  try {
    const user = UserModel.getInstance();

    const storedTrackedFlags = localStorage.getItem('trackedFlags');
    const trackedFlags = storedTrackedFlags ? JSON.parse(storedTrackedFlags) : {};

    const trackedFlagKey = `${decision.userContext.id}-${flagName}`;
    if (trackedFlags[trackedFlagKey]) {
      return;
    }

    user.events.trackAction('experiment-entered', {
      'user-id': user.id || '',
      'flag-key': flagName,
      platform: 'web',
      'variation-key': decision.variationKey || '',
      'rule-key': decision.ruleKey || '',
      'plan-name': user.subscriptionOrderSku || ''
    });

    trackedFlags[trackedFlagKey] = true;
    localStorage.setItem('trackedFlags', JSON.stringify(trackedFlags));
  } catch (error) {
    Rollbar.trackError('Failed to handle experiment tracking:', error, {
      flagName,
      decision
    });
  }
};
